import React, {useContext, useEffect, useState} from 'react';
import {DatePicker, Divider, Form, Select} from "antd";
import dayjs from 'dayjs';
import SummaryCard from "./SummaryCard";
import StudentReportActivity from "./StudentReportActivity";
import BreadcrumbAntd from "../../common/BreadcrumbAntd";
import {UserContext} from "../../common/UserContext";
import {useLocation, useSearchParams} from "wouter";
import {useBreadcrumbComponent} from "../../common/useBreadcrumbComponent";
import axios from "../../common/axios";
import CircularLoader from "../../common/CircularLoader";
import GenerateAIStudentReportDialog from "./GenerateAIStudentReportDialog";
import Button from "../../common/Button";
import {AutoAwesomeRounded} from "@mui/icons-material";
import TableFilterControls from "../../common/TableFilterControls";
import TableFilterControlsSection from "../../common/TableFilterControlsSection";
import TableFilterControlsForm from "../../common/TableFilterControlsForm";
import PageHeader from "../../common/PageHeader";

const { RangePicker } = DatePicker;

function StudentReport({ studentIdParam }) {
    const { user } = useContext(UserContext);
    const [studentReport, setStudentReport] = useState();
    const [onlyShowOptions, setOnlyShowOptions] = useState([]);
    const [searchParams] = useSearchParams();
    const [dateRange, setDateRange] = useState(() => {
        const startParam = searchParams.get('startDate');
        const endParam = searchParams.get('endDate');

        if (startParam && endParam) {
            return [dayjs(startParam), dayjs(endParam)];
        }
        return [dayjs().subtract(6, 'day'), dayjs()];
    });
    const [location, navigate] = useLocation();
    const BreadcrumbBar = useBreadcrumbComponent({ dropLast: false, dropEndingSeparator: true });
    const currentStudentId = studentIdParam;
    const [loading, setLoading] = useState(true);
    const useAIGeneratorUI = false;
    const [isGenerateAIStudentReportDialogOpen, setGenerateAIStudentReportDialogOpen] = useState(false);

    const presetRanges = {
        Today: [dayjs(), dayjs()],
        'This Week': [dayjs().startOf('week'), dayjs().endOf('week')],
        'Last 7 Days': [dayjs().subtract(6, 'day'), dayjs()],
        'This Month': [dayjs().startOf('month'), dayjs().endOf('month')],
    };

    const handleOnlyShowChange = (value) => {
        setOnlyShowOptions(value);
    };

    const showSessionsWithActivity = onlyShowOptions.includes('sessions_with_activity');

    const loadReportData = () => {
        setLoading(true);
        const [start, end] = dateRange || [];

        const isUSLocale = user.locale === 'en_US';

        let formattedStart, formattedEnd;

        if (start) {
            const day = start.date().toString().padStart(2, '0');
            const month = (start.month() + 1).toString().padStart(2, '0');
            const year = start.year();
            formattedStart = isUSLocale ? `${month}/${day}/${year}` : `${day}/${month}/${year}`;
        }

        if (end) {
            const day = end.date().toString().padStart(2, '0');
            const month = (end.month() + 1).toString().padStart(2, '0');
            const year = end.year();
            formattedEnd = isUSLocale ? `${month}/${day}/${year}` : `${day}/${month}/${year}`;
        }

        axios.get('/web/ui/report/teacher/activity/studentSummary', {
            params: {
                studentId: studentIdParam,
                ...(formattedStart && { startSearch: formattedStart }),
                ...(formattedEnd && { endSearch: formattedEnd }),
                ...((formattedStart && formattedEnd) && { searchPeriod: 'DATE_RANGE' })
            }
        })
            .then(response => {
                if (response.data) {
                    if(response.data.reportData.length > 0){
                        setStudentReport(response.data?.reportData[0]);
                    }
                } else {
                    console.error('Failed to load report: No data received');
                }
            })
            .catch(error => {
                console.error('Error fetching report:', error);
            })
            .finally(() => {
                setLoading(false)
            });
    };

    const generateReadableLastLogin = (epochTime) => {
        if (!epochTime) return 'No login recorded'
        const date = new Date(epochTime);
        return date.toLocaleString();
    };

    const generateActivityCount = (report) => {
        if(!report) return;
        const { countTests, countScorchers, countWalkthroughs } = report;
        return countTests + countScorchers + countWalkthroughs;
    }

    useEffect(() => {
        loadReportData();
    }, [dateRange]);

    if(!user || !studentReport || typeof user.getDateFormatString !== 'function'){
        return(
            <CircularLoader />
        )
    }

    return (
        <div>
            <BreadcrumbBar />
            <CircularLoader visible={loading} pageCentred={false} displayInElement={true} />
            {!loading &&
                <>
                    <PageHeader
                        title={studentReport?.name}
                        buttons={
                        <>
                            {useAIGeneratorUI &&
                                <Button
                                    type={'outline'}
                                    onClick={() => setGenerateAIStudentReportDialogOpen(true)}>
                                    <AutoAwesomeRounded style={{ marginRight: '0.5rem' }} /> Generate Summary
                                </Button>
                            }
                        </>
                        }
                    />
                    <SummaryCard
                        topSummary={[
                            {value: studentReport?.countSessions, label: 'Sessions started'},
                            {value: generateActivityCount(studentReport), label: 'Activities'},
                            {value: studentReport?.countQuestions, label: 'Questions complete'},
                        ]}
                        bottomSummary={[
                            {value: generateReadableLastLogin(studentReport?.lastLogin), label: 'Last login'}
                        ]}
                    />
                </>
            }
            <Divider />
            <TableFilterControls
                filterContent={
                    <TableFilterControlsForm>
                        <TableFilterControlsSection showDivider={false}>
                            <Form.Item label={<span>Date range</span>}>
                                <RangePicker
                                    presets={Object.entries(presetRanges).map(([label, range]) => ({
                                        label,
                                        value: range,
                                    }))}
                                    value={dateRange}
                                    onChange={setDateRange}
                                    format={user.getDateFormatString()}
                                    style={{width: '100%'}}
                                />
                            </Form.Item>
                            <Form.Item label={<span>Only show</span>} name='Only show'>
                                <Select
                                    mode="multiple"
                                    placeholder={'Report display options'}
                                    allowClear={true}
                                    value={onlyShowOptions}
                                    onChange={handleOnlyShowChange}
                                >
                                    <Select.Option value="sessions_with_activity">Sessions with activity</Select.Option>
                                </Select>
                            </Form.Item>
                        </TableFilterControlsSection>
                    </TableFilterControlsForm>

                }
            />
            <StudentReportActivity
                currentStudentId={currentStudentId}
                dateRange={dateRange}
                showSessionsWithActivity={showSessionsWithActivity}
            />
            {useAIGeneratorUI &&
                <GenerateAIStudentReportDialog
                    open={isGenerateAIStudentReportDialogOpen}
                    handleDialogOpen={() => setGenerateAIStudentReportDialogOpen(false)}
                    student={studentReport}
                />
            }
        </div>
    );
}

export default StudentReport;
